import ScriptedTransportBase from "./scriptedTransportBase";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";
import buildOnboardingScenario, {
  emitWelcome,
  emitResume,
  emitCurrentStep
} from "./onboardingScenario";
import buildDigestScenario, { emitDigest } from "./digestScenario";
import { fetchProspectPage } from "./onboardingServices";
import SseAgentTransport from "./SseAgentTransport";
import { AGENT_EVENTS, CLIENT_EVENTS } from "./agentContract";
import { getCopilotContext } from "./copilotContext";
import userDetail from "Stores/userDetail";

/**
 * Client-side scripted transport for the copilot onboarding flows. Reuses the
 * shared step-engine (scriptedTransportBase); each step's run() awaits real
 * service calls. The deterministic state machine, gating and resume live in
 * onboardingCopilotStore, which is passed as the engine ctx so steps read and
 * write the collected artifacts. Selected by createAgentTransport(mode).
 *
 * Hybrid: the scripted flow owns progression, but a free-text question the
 * current step does not expect is routed to the live MCP copilot (a lazily
 * created SseAgentTransport "consultant") for a read-only answer, after which
 * the current step is re-emitted so the flow stays on the rail.
 */
class OnboardingAgentTransport extends ScriptedTransportBase {
  constructor(mode = "onboarding") {
    const digest = mode === "digest";
    super({
      steps: digest ? buildDigestScenario() : buildOnboardingScenario(),
      ctx: onboardingCopilotStore,
      // Only the digest keeps the canned nudge; the new-user flow answers
      // off-script via the consultant (offScriptHandler set below).
      fallbackText:
        "Use the buttons above to keep going. You can also close this and explore on your own any time.",
      onConnected: async api => {
        if (digest) return emitDigest(api);
        onboardingCopilotStore.hydrate(userDetail?.userInfo?.memberId);
        const resuming =
          onboardingCopilotStore.status === "active" && onboardingCopilotStore.step > 0;
        return resuming ? emitResume(api) : emitWelcome(api);
      }
    });
    this.mode = mode;
    this._consultant = null;
    this._consultantOff = null;
    this._pendingConsultant = null;

    if (!digest) {
      this._offScriptHandler = (api, evt, ctx) => this._handleOffScript(api, evt, ctx);
    }

    // Tear the consultant down with the onboarding transport so its EventSource
    // does not outlive the flow.
    const baseConnect = this.connect;
    this.connect = (conversationId, onEvent) => {
      const off = baseConnect(conversationId, onEvent);
      return () => {
        off();
        this._teardownConsultant();
      };
    };
  }

  // The Step 4 prospect TABLE_REF opens the shared TableSidePanel, which pages
  // through the transport. Onboarding serves the already-loaded search results
  // as one page (see fetchProspectPage).
  fetchTablePage = () => fetchProspectPage();

  interrupt = () => {
    this.interrupted = true;
    if (this._consultant && this._consultant.interrupt) this._consultant.interrupt();
  };

  _teardownConsultant = () => {
    try {
      if (this._consultantOff) this._consultantOff();
    } catch (e) {
      /* best-effort */
    }
    this._consultant = null;
    this._consultantOff = null;
    this._pendingConsultant = null;
  };

  // Answer an off-script question with the live MCP copilot, then re-emit the
  // current step. Read-only: the consultant answers; it never advances the
  // flow or mutates data (see the onboarding context flag + the MCP guard).
  _handleOffScript = async (api, evt, ctx) => {
    const text = (evt.text || "").trim();
    if (!text) return;
    try {
      await this._askConsultant(text, ctx);
    } catch (e) {
      /* consultant failed; still return the user to their step */
    }
    if (this.disconnected) return;
    await emitCurrentStep(api, ctx);
  };

  _askConsultant = (text, ctx) =>
    new Promise(resolve => {
      if (this.disconnected) {
        resolve();
        return;
      }
      if (!this._consultant) {
        this._consultant = new SseAgentTransport();
        this._consultantOff = this._consultant.connect(null, evt => {
          // Forward the answer into the same thread the scripted flow uses.
          if (this.onEvent) this.onEvent(evt);
          if (
            evt.type === AGENT_EVENTS.TURN_END ||
            evt.type === AGENT_EVENTS.AGENT_ERROR
          ) {
            const done = this._pendingConsultant;
            this._pendingConsultant = null;
            if (done) done();
          }
        });
      }
      this._pendingConsultant = resolve;
      // Safety net: never hang the flow if the answer turn never ends.
      const guard = setTimeout(() => {
        if (this._pendingConsultant === resolve) {
          this._pendingConsultant = null;
          resolve();
        }
      }, 60000);
      const wrapped = () => {
        clearTimeout(guard);
        resolve();
      };
      this._pendingConsultant = wrapped;

      let base = {};
      try {
        base = getCopilotContext() || {};
      } catch (e) {
        base = {};
      }
      this._consultant.send({
        type: CLIENT_EVENTS.USER_MESSAGE,
        text,
        // Tells the orchestrator the user is mid-onboarding: answer briefly,
        // read-only, and do not take over the flow.
        context: {
          ...base,
          onboarding: { active: true, step: ctx.step, awaiting: ctx.awaiting }
        }
      });
    });
}

export default OnboardingAgentTransport;
